import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileImage, Upload, Trash2, CheckCircle2, Loader2, AlertCircle, RotateCw, X, Clock } from "lucide-react";
import { listFlyers, createFlyer, getFlyer, applyFlyer, deleteFlyer, euro } from "@/lib/store";

const ACCEPTED = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const STATUS_META = {
  pending: { label: "In attesa", cls: "text-zinc-400", Icon: Clock },
  uploading: { label: "Caricamento", cls: "text-[#2563EB]", Icon: Loader2 },
  done: { label: "Caricata", cls: "text-emerald-400", Icon: CheckCircle2 },
  error: { label: "Errore", cls: "text-rose-400", Icon: AlertCircle },
};

export default function Volantini() {
  const [flyers, setFlyers] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = () => listFlyers().then(setFlyers).catch(() => {});
  useEffect(() => { load(); }, []);
  // revoca gli object URL delle anteprime allo smontaggio
  useEffect(() => () => { queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview)); }, [queue]);

  const patchItem = (uid, patch) =>
    setQueue((q) => q.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));

  // carica un singolo elemento della coda (riusato anche dal retry)
  const uploadOne = async (item) => {
    patchItem(item.uid, { status: "uploading", error: null });
    try {
      const data_b64 = await fileToB64(item.file);
      const r = await createFlyer({ filename: item.file.name, mime: item.file.type, data_b64 });
      patchItem(item.uid, { status: "done", flyerId: r.id, itemsCount: r.items_count });
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Caricamento fallito";
      patchItem(item.uid, { status: "error", error: msg });
      return { ok: false };
    }
  };

  // aggiunge i file alla coda e li invia in batch (sequenziale per non sovraccaricare l'estrazione AI)
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const accepted = [];
    const rejected = [];
    files.forEach((f) => (ACCEPTED.includes(f.type) ? accepted.push(f) : rejected.push(f.name)));
    if (rejected.length) toast.error(`Formato non supportato: ${rejected.join(", ")}`, { description: "Usa PDF/JPG/PNG/WEBP" });
    if (accepted.length === 0) return;

    const newItems = accepted.map((f) => ({
      uid: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      status: "pending",
      error: null,
    }));
    setQueue((q) => [...newItems, ...q]);

    setBusy(true);
    let okCount = 0;
    for (const item of newItems) {
      const res = await uploadOne(item);
      if (res.ok) okCount += 1;
      await load();
    }
    setBusy(false);
    if (okCount > 0) toast.success(`${okCount}/${newItems.length} volantini analizzati`);
    if (okCount < newItems.length) toast.error(`${newItems.length - okCount} file non caricati: puoi ritentare dalla lista`);
  };

  const onInputChange = async (e) => {
    await handleFiles(e.target.files);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    await handleFiles(e.dataTransfer?.files);
  };

  const retryOne = async (item) => {
    setBusy(true);
    const res = await uploadOne(item);
    await load();
    setBusy(false);
    if (res.ok) toast.success(`${item.file.name} caricato`);
  };

  const removeFromQueue = (uid) => {
    setQueue((q) => {
      const target = q.find((it) => it.uid === uid);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return q.filter((it) => it.uid !== uid);
    });
  };

  const clearDone = () => {
    setQueue((q) => {
      q.filter((it) => it.status === "done" && it.preview).forEach((it) => URL.revokeObjectURL(it.preview));
      return q.filter((it) => it.status !== "done");
    });
  };

  const open = async (id) => setSel(await getFlyer(id));
  const apply = async (id) => {
    setApplyBusy(true);
    try {
      const r = await applyFlyer(id, { all: true, create_offers: true, apply_promo: true });
      toast.success(r.note, { description: `${r.offerte_create} offerte · ${r.promo_applicate} promo applicate` });
      load(); setSel(await getFlyer(id));
    } catch { toast.error("Applicazione fallita"); } finally { setApplyBusy(false); }
  };
  const del = async (id) => { await deleteFlyer(id); toast.success("Volantino eliminato"); setSel(null); load(); };

  const doneCount = queue.filter((q) => q.status === "done").length;

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="volantini-panel">
      <div className="space-y-3">
        {/* Dropzone upload multiplo */}
        <div
          data-testid="flyer-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-xl border border-dashed p-6 text-center transition-colors ${dragOver ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/20 bg-[#11141A]"}`}
        >
          <FileImage className="mx-auto h-8 w-8 text-[#2563EB]" />
          <p className="mt-2 text-sm text-zinc-300">Carica uno o più volantini promozionali</p>
          <p className="text-xs text-zinc-500">PDF, JPG, PNG o WEBP · trascina qui i file o selezionali · l'AI estrae marca, modello e prezzi</p>
          <input ref={fileRef} data-testid="flyer-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onInputChange} className="hidden" />
          <button data-testid="flyer-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Upload className="h-4 w-4" /> {busy ? "Analisi in corso…" : "Seleziona immagini"}
          </button>
        </div>

        {/* Griglia coda upload con stato per-file */}
        {queue.length > 0 && (
          <div data-testid="flyer-upload-queue" className="rounded-xl border border-white/10 bg-[#11141A] p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Coda upload ({queue.length})</h3>
              {doneCount > 0 && (
                <button data-testid="flyer-clear-done" onClick={clearDone} className="text-xs text-zinc-400 hover:text-white">Rimuovi completati</button>
              )}
            </div>
            <div className="space-y-2">
              {queue.map((it) => {
                const meta = STATUS_META[it.status];
                const Icon = meta.Icon;
                return (
                  <div key={it.uid} data-testid="flyer-queue-item" className="flex items-center gap-3 rounded-lg border border-white/5 bg-[#0B0E13] p-2">
                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-black/40">
                      {it.preview
                        ? <img src={it.preview} alt={it.file.name} className="h-full w-full object-cover" />
                        : <div className="flex h-full w-full items-center justify-center text-zinc-500"><FileImage className="h-5 w-5" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-white" title={it.file.name}>{it.file.name}</p>
                      <div className="mt-0.5 flex items-center gap-2 text-xs">
                        <span className="text-zinc-500">{fmtSize(it.file.size)}</span>
                        <span className={`inline-flex items-center gap-1 font-medium ${meta.cls}`} data-testid={`flyer-queue-status-${it.status}`}>
                          <Icon className={`h-3 w-3 ${it.status === "uploading" ? "animate-spin" : ""}`} /> {meta.label}
                        </span>
                        {it.status === "done" && typeof it.itemsCount === "number" && (
                          <span className="text-zinc-500">· {it.itemsCount} promo</span>
                        )}
                      </div>
                      {it.status === "error" && it.error && (
                        <p className="mt-0.5 truncate text-xs text-rose-400" title={it.error}>{it.error}</p>
                      )}
                    </div>
                    {it.status === "error" && (
                      <button data-testid="flyer-queue-retry" disabled={busy} onClick={() => retryOne(it)}
                        className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1 text-xs text-zinc-300 hover:border-[#2563EB] disabled:opacity-50">
                        <RotateCw className="h-3 w-3" /> Riprova
                      </button>
                    )}
                    {it.status !== "uploading" && (
                      <button data-testid="flyer-queue-remove" onClick={() => removeFromQueue(it.uid)} className="rounded-full p-1 text-zinc-500 hover:text-white">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Volantini caricati</h3>
        {flyers.length === 0 && <p className="text-sm text-zinc-500">Nessun volantino ancora.</p>}
        {flyers.map((f) => (
          <button key={f.id} data-testid="flyer-row" onClick={() => open(f.id)}
            className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm ${sel?.id === f.id ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/10 bg-[#11141A]"}`}>
            <span className="flex-1 truncate text-white">{f.filename}</span>
            <span className="text-xs text-zinc-500">{f.items_count} promo</span>
            {f.applied ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-400">Applicato</span>
              : <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">Da rivedere</span>}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#11141A] p-4">
        {!sel ? <p className="text-zinc-500">Seleziona o carica un volantino.</p> : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <h4 className="truncate font-semibold text-white">{sel.filename}</h4>
              <div className="flex gap-2">
                {!sel.applied && <button data-testid="flyer-apply-btn" disabled={applyBusy || !sel.items?.length} onClick={() => apply(sel.id)} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" /> Applica</button>}
                <button data-testid="flyer-delete-btn" onClick={() => del(sel.id)} className="rounded-full border border-white/10 px-2 py-1.5 text-xs text-zinc-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            {(!sel.items || sel.items.length === 0) ? <p className="text-sm text-zinc-500">Nessuna promo estratta dal volantino. Prova con un file più leggibile.</p> : (
              <div className="max-h-[60vh] space-y-2 overflow-auto">
                {sel.items.map((it) => (
                  <div key={it.id} data-testid="flyer-item" className="rounded-lg border border-white/5 bg-[#0B0E13] p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{[it.brand, it.model, it.storage, it.color].filter(Boolean).join(" ")}</span>
                      {it.approved && <span className="ml-auto text-emerald-400">✓</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-zinc-400">
                      {it.old_price_cents && <span className="line-through">{euro(it.old_price_cents)}</span>}
                      {it.promo_price_cents && <span className="font-semibold text-emerald-400">{euro(it.promo_price_cents)}</span>}
                      <span>{it.matched_slug ? `↔ ${it.matched_name}` : "nessun match a catalogo"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-zinc-500">Applicando: le promo con match aggiornano il prezzo promo a catalogo e vengono pubblicate nel carosello della home.</p>
          </>
        )}
      </div>
    </div>
  );
}
